<template>
  <div v-if="isReadAlongMode && taskType !== 'tts' && displayMode === 'subtitles'" class="read-along-container">
    <div
      v-if="showLiveCaption"
      class="live-caption"
      :class="{ 'is-fallback': isLiveRecognitionUnavailable }"
      aria-live="polite"
    >
      <span class="live-caption-mode">
        <span class="live-caption-dot" aria-hidden="true"></span>
        {{ isLiveRecognitionUnavailable ? $t('audioPlayer.liveRecognitionFallback') : $t('audioPlayer.liveRecognition') }}
      </span>
      <span class="live-caption-text">{{ liveCaptionText }}</span>
    </div>
    <div
      class="read-along-main"
      :class="{
        'is-waiting': waitingForRecording,
        'is-recording': isRecording,
        'read-mode-following': readAlongMode === 'following',
        'read-mode-recording': readAlongMode !== 'following',
      }"
    >
      <div ref="modeMenu" class="mode-menu">
        <button
          class="mode-menu-trigger"
          type="button"
          aria-haspopup="menu"
          :aria-expanded="showModeMenu"
          :aria-label="$t(`audioPlayer.${readAlongMode}Mode`)"
          @click.stop="showModeMenu = !showModeMenu"
        >
          <span :class="['read-icon', modeIcons[readAlongMode]]"></span>
          <span class="mode-menu-label">{{ $t(`audioPlayer.${readAlongMode}Mode`) }}</span>
          <span class="mode-menu-caret" aria-hidden="true"></span>
        </button>
        <Transition name="slide-fade">
          <div v-if="showModeMenu" class="mode-menu-popup" role="menu" @click.stop>
            <button
              v-for="mode in ['recording', 'following', 'retelling']"
              :key="mode"
              class="mode-menu-item"
              :class="{ active: readAlongMode === mode }"
              type="button"
              role="menuitemradio"
              :aria-checked="readAlongMode === mode"
              @click="selectMode(mode)"
            >
              <span :class="['read-icon', modeIcons[mode]]"></span>
              <span class="mode-menu-copy">
                <strong>{{ $t(`audioPlayer.${mode}Mode`) }}</strong>
                <small>{{ $t(`audioPlayer.${mode}ModeTooltip`) }}</small>
              </span>
              <span v-if="readAlongMode === mode" class="read-icon icon-check" aria-hidden="true"></span>
            </button>
          </div>
        </Transition>
      </div>

      <div class="read-along-stage" :class="`state-${creatureState}`" aria-live="polite">
        <span
          class="creature-frame"
          :class="{ 'is-counting': pauseProgress > 0 }"
          :style="{ '--pause-progress': pauseProgress }"
        >
          <SoundCreature :state="creatureState" :label="creatureLabel" :level="microphoneLevel" :bars="microphoneBars" />
          <span class="pause-ring" aria-hidden="true"></span>
        </span>
        <div class="read-along-stage-copy">
          <strong>{{ creatureLabel }}</strong>
        </div>
      </div>

      <template v-if="readAlongMode === 'following'">
        <div ref="micSettings" class="mic-settings">
          <button
            class="btn btn-icon recording-tool-button"
            :aria-label="$t('audioPlayer.readAlongSettings')"
            :title="$t('audioPlayer.readAlongSettings')"
            :aria-expanded="showVadSettings"
            @click.stop="toggleVadSettings"
          >
            <span class="read-icon icon-settings"></span>
          </button>
          <Transition name="slide-fade">
            <div class="slider-popup vad-settings" v-if="showVadSettings" @click.stop>
              <section class="vad-group">
                <span class="vad-group-title">{{ $t('audioPlayer.followingMode') }}</span>
                <div class="vad-slider">
                  <div class="vad-slider-head">
                    <label for="following-interval">{{ $t('audioPlayer.followingInterval') }}</label>
                    <output for="following-interval">{{ followingInterval }}s</output>
                  </div>
                  <input
                    id="following-interval"
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    :value="followingInterval"
                    @input="$emit('update:followingInterval', Number($event.target.value))"
                  />
                  <p class="vad-hint">{{ $t('audioPlayer.followingIntervalHint') }}</p>
                </div>
              </section>
            </div>
          </Transition>
        </div>
      </template>

      <template v-if="readAlongMode !== 'following' && isRecording">
        <div class="attempt-actions">
          <el-tooltip :content="$t('audioPlayer.readAlongFinishHint')" placement="top">
            <button class="btn attempt-button" type="button" @click="$emit('finish-speaking')">
              <span class="read-icon icon-check"></span>
              {{ $t('audioPlayer.readAlongFinish') }}
            </button>
          </el-tooltip>
        </div>
      </template>

      <template v-else-if="readAlongMode !== 'following'">
        <div class="recording-tools">
          <div class="recording-library-summary" aria-live="polite">
            <strong>{{ $t('audioPlayer.recordingCount', { count: recordingCount }) }}</strong>
            <span>{{ formattedRecordingTime }}</span>
          </div>
          <el-tooltip v-if="isPlayingRecordings" :content="$t('audioPlayer.stopRecordings')" placement="top">
            <button
              class="btn btn-icon recording-tool-button active"
              :aria-label="$t('audioPlayer.stopRecordings')"
              @click="$emit('play-all-recordings')"
            >
              <span class="read-icon icon-pause"></span>
            </button>
          </el-tooltip>
          <div ref="micSettings" class="mic-settings">
            <button
              class="btn btn-icon recording-tool-button"
              :aria-label="$t('audioPlayer.readAlongSettings')"
              :title="$t('audioPlayer.readAlongSettings')"
              :aria-expanded="showVadSettings"
              @click.stop="toggleVadSettings"
            >
              <span class="read-icon icon-settings"></span>
            </button>
            <Transition name="slide-fade">
              <div class="slider-popup vad-settings" v-if="showVadSettings" @click.stop>
                <section class="vad-group">
                  <div class="vad-group-head">
                    <span class="vad-group-title">{{ $t('audioPlayer.microphoneInput') }}</span>
                    <button
                      class="vad-link-button"
                      type="button"
                      :disabled="isLoadingMicrophones"
                      @click="$emit('refresh-microphones')"
                    >
                      {{ isLoadingMicrophones ? $t('audioPlayer.microphoneRefreshing') : $t('audioPlayer.microphoneRefresh') }}
                    </button>
                  </div>
                  <el-select
                    class="microphone-device-select"
                    :aria-label="$t('audioPlayer.microphoneInput')"
                    :placeholder="$t('audioPlayer.microphoneSystemDefault')"
                    :model-value="selectedMicrophoneId"
                    :disabled="isLoadingMicrophones"
                    :fit-input-width="true"
                    :fallback-placements="['top-end', 'bottom-end']"
                    filterable
                    placement="top-end"
                    :offset="8"
                    popper-class="player-select-popper"
                    @change="$emit('update:selectedMicrophoneId', $event)"
                  >
                    <el-option
                      value=""
                      :label="$t('audioPlayer.microphoneSystemDefault')"
                    />
                    <el-option
                      v-for="(device, index) in microphoneDevices"
                      :key="device.deviceId"
                      :value="device.deviceId"
                      :label="device.label || $t('audioPlayer.microphoneDeviceNumber', { number: index + 1 })"
                    />
                  </el-select>
                </section>

                <section v-if="readAlongMode === 'recording'" class="vad-group">
                  <span class="vad-group-title">{{ $t('audioPlayer.practiceLanguage') }}</span>
                  <el-select
                    class="microphone-device-select"
                    :aria-label="$t('audioPlayer.practiceLanguage')"
                    :model-value="assessmentLanguage"
                    :fit-input-width="true"
                    :fallback-placements="['top-end', 'bottom-end']"
                    filterable
                    placement="top-end"
                    :offset="8"
                    popper-class="player-select-popper"
                    @change="$emit('update:assessmentLanguage', $event)"
                  >
                    <el-option
                      v-for="language in pronunciationLanguages"
                      :key="language.value"
                      :value="language.value"
                      :label="language.label"
                    />
                  </el-select>
                  <p class="vad-hint">{{ $t('audioPlayer.practiceLanguageHint') }}</p>
                </section>

                <section v-if="readAlongMode === 'recording'" class="vad-group">
                  <button
                    class="vad-toggle"
                    type="button"
                    role="switch"
                    :aria-checked="assessmentEnabled"
                    @click="$emit('update:assessmentEnabled', !assessmentEnabled)"
                  >
                    <span class="vad-toggle-copy">
                      <strong>{{ $t('audioPlayer.pronunciationScoring') }}</strong>
                      <small>{{ $t('audioPlayer.pronunciationScoringHint') }}</small>
                    </span>
                    <span class="vad-switch" :class="{ active: assessmentEnabled }" aria-hidden="true">
                      <span></span>
                    </span>
                  </button>
                  <p v-if="liveRecognitionStatus === 'unavailable'" class="vad-status">{{ unavailableText }}</p>
                </section>

                <section v-if="readAlongMode === 'recording'" class="vad-group">
                  <span class="vad-group-title">{{ $t('audioPlayer.vadPauseDelay') }}</span>
                  <div class="vad-slider">
                    <div class="vad-slider-head">
                      <label for="vad-unfinished-pause">{{ $t('audioPlayer.unfinishedPause') }}</label>
                      <output for="vad-unfinished-pause">{{ (unfinishedPauseMs / 1000).toFixed(1) }}s</output>
                    </div>
                    <input
                      id="vad-unfinished-pause"
                      type="range"
                      min="1000"
                      max="10000"
                      step="500"
                      :value="unfinishedPauseMs"
                      @input="$emit('update:unfinishedPauseMs', Number($event.target.value))"
                    />
                  </div>
                  <div class="vad-slider">
                    <div class="vad-slider-head">
                      <label for="vad-finish-pause">{{ $t('audioPlayer.finishPause') }}</label>
                      <output for="vad-finish-pause">{{ (vadRedemptionMs / 1000).toFixed(1) }}s</output>
                    </div>
                    <input
                      id="vad-finish-pause"
                      type="range"
                      min="400"
                      max="2500"
                      step="100"
                      :value="vadRedemptionMs"
                      @input="$emit('update:vadRedemptionMs', Number($event.target.value))"
                    />
                  </div>
                  <p class="vad-hint">
                    {{ isLiveRecognitionUnavailable ? $t('audioPlayer.vadPauseDelayDurationHint') : $t('audioPlayer.vadPauseDelayHint') }}
                  </p>
                </section>

                <section class="vad-group">
                  <span class="vad-group-title">{{ $t('audioPlayer.myRecordings') }}</span>
                  <div class="vad-actions">
                    <button
                      class="vad-action-button"
                      type="button"
                      :disabled="recordingCount === 0"
                      @click="$emit('play-all-recordings')"
                    >
                      <span :class="['read-icon', isPlayingRecordings ? 'icon-pause' : 'icon-play']"></span>
                      {{ isPlayingRecordings ? $t('audioPlayer.stopRecordings') : $t('audioPlayer.playRecordings') }}
                    </button>
                    <button
                      class="vad-action-button"
                      type="button"
                      :disabled="recordingCount === 0"
                      @click="$emit('download-recordings')"
                    >
                      <span class="read-icon icon-download"></span>
                      {{ $t('audioPlayer.downloadRecordings') }}
                    </button>
                  </div>
                  <p class="vad-hint">{{ $t('audioPlayer.recordingStorageHint') }}</p>
                </section>
              </div>
            </Transition>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SoundCreature from './SoundCreature.vue'
import { PRONUNCIATION_LANGUAGE_OPTIONS } from '@/composables/player/pronunciationLanguages'

export default {
  name: 'PlayerReadAlongPanel',
  components: { SoundCreature },
  emits: [
    'update:readAlongMode',
    'update:vadRedemptionMs',
    'update:unfinishedPauseMs',
    'update:assessmentEnabled',
    'update:assessmentLanguage',
    'update:selectedMicrophoneId',
    'update:followingInterval',
    'refresh-microphones',
    'play-all-recordings',
    'download-recordings',
    'finish-speaking',
  ],
  props: {
    isReadAlongMode: { type: Boolean, default: false },
    taskType: { type: String, required: true },
    displayMode: { type: String, required: true },
    readAlongMode: { type: String, required: true },
    followingInterval: { type: Number, default: 1 },
    isRecording: { type: Boolean, default: false },
    microphoneLevel: { type: Number, default: 0 },
    microphoneBars: { type: Array, default: () => [] },
    waitingForRecording: { type: Boolean, default: false },
    isPlaying: { type: Boolean, default: false },
    isPaused: { type: Boolean, default: false },
    isWaitingForShadowing: { type: Boolean, default: false },
    vadRedemptionMs: { type: Number, default: 800 },
    unfinishedPauseMs: { type: Number, default: 3500 },
    pauseProgress: { type: Number, default: 0 },
    isHoldingForUnfinished: { type: Boolean, default: false },
    liveTranscript: { type: String, default: '' },
    isLiveCaptionLingering: { type: Boolean, default: false },
    liveRecognitionStatus: { type: String, default: 'pending' },
    liveRecognitionUnavailableReason: { type: String, default: '' },
    assessmentEnabled: { type: Boolean, default: true },
    assessmentLanguage: { type: String, default: 'en-US' },
    microphoneDevices: { type: Array, default: () => [] },
    selectedMicrophoneId: { type: String, default: '' },
    isLoadingMicrophones: { type: Boolean, default: false },
    recordingCount: { type: Number, default: 0 },
    totalRecordingTime: { type: Number, default: 0 },
    currentRecordingTime: { type: Number, default: 0 },
    isPlayingRecordings: { type: Boolean, default: false },
  },
  data() {
    return {
      showVadSettings: false,
      showModeMenu: false,
      pronunciationLanguages: PRONUNCIATION_LANGUAGE_OPTIONS,
      modeIcons: { recording: 'icon-microphone', following: 'icon-headphones', retelling: 'icon-edit' },
      hasPlaybackStarted: false,
    }
  },
  watch: {
    isPlaying(isPlaying) {
      if (isPlaying) this.hasPlaybackStarted = true
    },
    isReadAlongMode(isReadAlongMode) {
      if (!isReadAlongMode) this.hasPlaybackStarted = false
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.handleSettingsPointerDown)
    document.addEventListener('keydown', this.handleSettingsKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.handleSettingsPointerDown)
    document.removeEventListener('keydown', this.handleSettingsKeydown)
  },
  computed: {
    creatureState() {
      if (this.isPaused && this.hasPlaybackStarted && !this.waitingForRecording && !this.isRecording) return 'paused'
      if (this.readAlongMode === 'following') {
        if (this.isPlaying) return 'listening'
        if (this.isWaitingForShadowing) return 'waiting'
        return 'idle'
      }
      if (this.isRecording) return 'speaking'
      if (this.waitingForRecording) return 'ready'
      if (this.isPlaying) return 'listening'
      return 'idle'
    },
    creatureLabel() {
      const zh = String(this.$i18n.locale).toLowerCase().startsWith('zh')
      if (this.isRecording && this.isHoldingForUnfinished) return this.$t('audioPlayer.readAlongStillListening')
      if (this.creatureState === 'paused') return zh ? '已暂停' : 'Paused'
      if (this.creatureState === 'speaking') return zh ? '录音' : 'Recording'
      if (['ready', 'waiting'].includes(this.creatureState)) return zh ? '开口' : 'Speak'
      return zh ? '先听' : 'Listen'
    },
    isLiveRecognitionUnavailable() {
      return ['unavailable', 'off'].includes(this.liveRecognitionStatus)
    },
    showLiveCaption() {
      if (this.readAlongMode !== 'recording') return false
      if (this.isRecording) return true
      return this.isLiveCaptionLingering && Boolean(this.liveTranscript.trim())
    },
    unavailableText() {
      if (this.liveRecognitionStatus === 'off') return this.$t('audioPlayer.liveRecognitionOff')
      if (this.liveRecognitionUnavailableReason === 'limit') return this.$t('audioPlayer.liveRecognitionLimit')
      return this.$t('audioPlayer.liveRecognitionError')
    },
    liveCaptionText() {
      if (this.isLiveRecognitionUnavailable) return this.unavailableText
      const text = this.liveTranscript.trim()
      if (!text) return this.$t('audioPlayer.liveRecognitionListening')
      // Keep the latest words visible when the sentence is long.
      return text.length > 64 ? `…${text.slice(-64)}` : text
    },
    formattedRecordingTime() {
      const value = this.isPlayingRecordings ? this.currentRecordingTime : this.totalRecordingTime
      const safeSeconds = Math.max(0, Number(value) || 0)
      const minutes = Math.floor(safeSeconds / 60)
      const seconds = Math.floor(safeSeconds % 60)
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    recordingStatusText() {
      if (this.isRecording) return this.$t('audioPlayer.readAlongRecording')
      if (this.waitingForRecording) return this.$t('audioPlayer.readAlongSpeakNow')
      return this.$t('audioPlayer.readAlongListenFirst')
    },
  },
  methods: {
    toggleVadSettings() {
      this.showVadSettings = !this.showVadSettings
    },
    closeVadSettings() {
      this.showVadSettings = false
    },
    selectMode(mode) {
      this.showModeMenu = false
      this.$emit('update:readAlongMode', mode)
    },
    handleSettingsPointerDown(event) {
      const target = event.target
      if (!this.$refs.modeMenu?.contains(target)) this.showModeMenu = false
      if (!this.showVadSettings) return

      if (this.$refs.micSettings?.contains(target)) return
      if (target?.closest?.('.player-select-popper')) return
      this.closeVadSettings()
    },
    handleSettingsKeydown(event) {
      if (event.key !== 'Escape') return
      this.closeVadSettings()
      this.showModeMenu = false
    },
  },
  watch: {
    readAlongMode() {
      this.closeVadSettings()
    },
    isReadAlongMode(newValue) {
      if (!newValue) this.closeVadSettings()
    },
    displayMode(newValue) {
      if (newValue !== 'subtitles') this.closeVadSettings()
    },
  },
}
</script>

<style scoped>
.read-along-container {
  position: absolute;
  right: 0;
  bottom: 80px;
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  justify-content: center;
  padding: 0 1rem;
  border: 0;
  background: transparent;
  pointer-events: none;
}

.read-along-main,
.read-along-main.read-mode-following {
  display: grid;
  grid-template-columns: auto minmax(110px, auto) auto;
  width: max-content;
  max-width: min(680px, 100%);
  min-height: 60px;
  margin: 0;
  padding: 0.375rem 0.75rem;
  gap: 0.625rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-float);
  pointer-events: auto;
}

@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
  .read-along-main,
  .read-along-main.read-mode-following {
    background: var(--surface-floating-glass);
    backdrop-filter: blur(18px) saturate(140%);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
  }
}

.read-along-main.is-waiting,
.read-along-main.is-recording {
  border-color: var(--border-default);
}

/* 模式切换：一个按钮显示当前模式，点开向上弹出菜单 */
.mode-menu {
  position: relative;
  justify-self: start;
}

.mode-menu-trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-subtle);
  color: var(--ink-primary);
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 160ms ease;
}

.mode-menu-trigger:hover,
.mode-menu-trigger[aria-expanded='true'] {
  background: var(--surface-panel);
}

.mode-menu-caret {
  width: 6px;
  height: 6px;
  margin-left: var(--space-1);
  border-right: 1.5px solid var(--ink-secondary);
  border-bottom: 1.5px solid var(--ink-secondary);
  /* 旋转后的尖角在方框上半部分，下移一点才和文字居中 */
  transform: translateY(2px) rotate(-135deg);
}

.mode-menu-popup {
  position: absolute;
  bottom: 100%;
  left: 0;
  z-index: 100;
  display: grid;
  gap: var(--space-1);
  width: min(320px, calc(100vw - 2rem));
  margin-bottom: var(--space-2);
  padding: var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
  transform-origin: bottom left;
}

.mode-menu-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.mode-menu-item .read-icon {
  margin-top: 2px;
}

.mode-menu-item:hover {
  background: var(--surface-subtle);
}

.mode-menu-item.active {
  background: var(--surface-active);
  color: var(--ink-primary);
}

.mode-menu-copy {
  display: grid;
  flex: 1;
  gap: 2px;
  min-width: 0;
}

.mode-menu-copy strong {
  color: var(--ink-primary);
  font-size: 0.875rem;
  font-weight: 600;
}

.mode-menu-copy small {
  color: var(--ink-secondary);
  font-size: 0.75rem;
  line-height: 1.5;
}

.mode-menu-trigger:focus-visible,
.mode-menu-item:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.read-along-stage {
  display: flex;
  align-items: center;
  justify-self: center;
  min-width: 0;
  gap: 0.55rem;
  padding: 0;
  border: 0;
}
.mic-settings {
  justify-self: end;
}

.live-caption {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  max-width: min(560px, 100%);
  min-height: 32px;
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-sm);
  pointer-events: auto;
}

@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
  .live-caption {
    background: var(--surface-floating-glass);
    backdrop-filter: blur(18px) saturate(140%);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
  }
}

.live-caption-mode {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  color: var(--signal-active-strong);
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.live-caption-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--signal-active);
}

.live-caption.is-fallback .live-caption-mode {
  color: var(--ink-secondary);
}

.live-caption.is-fallback .live-caption-dot {
  background: var(--ink-tertiary);
}

.live-caption-text {
  min-width: 0;
  overflow: hidden;
  color: var(--ink-primary);
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.live-caption.is-fallback .live-caption-text {
  color: var(--ink-secondary);
}


.creature-frame {
  position: relative;
  display: inline-grid;
  place-items: center;
}

/* Countdown while the learner is silent: fills up until the attempt ends. */
.pause-ring {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: conic-gradient(
    var(--signal-active) calc(var(--pause-progress, 0) * 360deg),
    var(--border-subtle) 0
  );
  opacity: 0;
  transition: opacity 160ms ease;
  pointer-events: none;
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
}

.creature-frame.is-counting .pause-ring {
  opacity: 1;
}

.attempt-actions {
  display: flex;
  align-items: center;
  justify-self: end;
  gap: var(--space-2);
}

.attempt-button {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: 36px;
  min-height: 36px;
  padding: 0 var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: none;
}

.attempt-button:hover {
  border-color: var(--border-active);
  background: var(--surface-subtle);
}

.attempt-button:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .pause-ring {
    transition: none;
  }
}

.recording-tools {
  display: flex;
  align-items: center;
  justify-self: end;
  gap: 0.25rem;
}

.recording-library-summary {
  display: grid;
  min-width: 4.5rem;
  margin-right: 0.25rem;
  line-height: 1.1;
}

.recording-library-summary strong {
  color: var(--ink-primary);
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.recording-library-summary span {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: 0.65rem;
}

.recording-tool-button {
  width: 36px;
  min-width: 36px;
  height: 36px;
  min-height: 36px;
  border: 0;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--ink-secondary);
  box-shadow: none;
}

.recording-tool-button:hover:not(:disabled),
.recording-tool-button.active {
  color: var(--signal-active-strong);
  background: var(--surface-active);
  border-color: transparent;
}

.recording-tool-button:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.read-along-stage-copy {
  display: grid;
  min-width: 3rem;
  gap: 0.16rem;
}

.read-along-stage-copy span {
  color: var(--ink-tertiary);
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1;
}

.read-along-stage-copy strong {
  color: var(--ink-primary);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
}

.read-along-stage.state-ready strong,
.read-along-stage.state-speaking strong {
  color: var(--signal-active-strong);
}

.read-icon {
  display: block;
  width: 15px;
  height: 15px;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

.icon-microphone { mask-image: url('~@/assets/ui-icons/microphone.svg'); -webkit-mask-image: url('~@/assets/ui-icons/microphone.svg'); }
.icon-headphones { mask-image: url('~@/assets/ui-icons/voice.svg'); -webkit-mask-image: url('~@/assets/ui-icons/voice.svg'); }
.icon-settings { mask-image: url('~@/assets/ui-icons/settings.svg'); -webkit-mask-image: url('~@/assets/ui-icons/settings.svg'); }
.icon-play { mask-image: url('~@/assets/ui-icons/play.svg'); -webkit-mask-image: url('~@/assets/ui-icons/play.svg'); }
.icon-pause { mask-image: url('~@/assets/ui-icons/pause.svg'); -webkit-mask-image: url('~@/assets/ui-icons/pause.svg'); }
.icon-edit { mask-image: url('~@/assets/ui-icons/edit.svg'); -webkit-mask-image: url('~@/assets/ui-icons/edit.svg'); }
.icon-check { mask-image: url('~@/assets/ui-icons/check.svg'); -webkit-mask-image: url('~@/assets/ui-icons/check.svg'); }
.icon-repeat { mask-image: url('~@/assets/ui-icons/repeat.svg'); -webkit-mask-image: url('~@/assets/ui-icons/repeat.svg'); }
.icon-download { mask-image: url('~@/assets/ui-icons/upload.svg'); -webkit-mask-image: url('~@/assets/ui-icons/upload.svg'); transform: rotate(180deg); }






.mic-settings .vad-settings {
  display: grid;
  gap: var(--space-4);
  width: min(320px, calc(100vw - 2rem));
  /* 弹窗向上展开，留出顶栏和底栏的高度，放不下时在弹窗内滚动 */
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  padding: var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
}

.vad-group {
  display: grid;
  gap: var(--space-2);
}

.vad-group + .vad-group {
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-subtle);
}

.vad-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}

.vad-group-title {
  color: var(--ink-primary);
  font-size: 0.82rem;
  font-weight: 600;
}

.vad-link-button {
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--signal-active-strong);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.vad-link-button:disabled {
  color: var(--ink-tertiary);
  cursor: wait;
}

.vad-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2);
}

.vad-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
}

.vad-action-button:hover:not(:disabled) {
  background: var(--surface-subtle);
}

.vad-action-button:disabled {
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.vad-hint {
  margin: 0;
  color: var(--ink-secondary);
  font-size: 0.75rem;
  line-height: 1.5;
}

.vad-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface-subtle);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.vad-toggle:hover {
  border-color: var(--border-default);
}

.vad-toggle-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.vad-toggle-copy strong {
  color: var(--ink-primary);
  font-size: 0.82rem;
  font-weight: 600;
}

.vad-toggle-copy small {
  color: var(--ink-secondary);
  font-size: 0.72rem;
  line-height: 1.45;
}

.vad-switch {
  position: relative;
  flex: 0 0 34px;
  width: 34px;
  height: 20px;
  border-radius: var(--radius-pill);
  background: var(--color-border-strong);
  transition: background 160ms ease;
}

.vad-switch span {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--surface-panel);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
  transition: transform 160ms ease;
}

.vad-switch.active {
  background: var(--signal-active-strong);
}

.vad-switch.active span {
  transform: translateX(14px);
}

.vad-status {
  margin: 0;
  color: var(--color-warning-text);
  font-size: 0.72rem;
  line-height: 1.45;
}

.vad-slider {
  display: grid;
  gap: var(--space-2);
  margin-top: var(--space-2);
}

.vad-slider-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.vad-slider-head label {
  color: var(--ink-primary);
  font-size: 0.8rem;
  font-weight: 600;
}

.vad-slider-head output {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: 0.8rem;
}

.vad-slider input[type='range'] {
  width: 100%;
  margin: 0;
  accent-color: var(--signal-active-strong);
  cursor: pointer;
}

.vad-link-button:focus-visible,
.vad-action-button:focus-visible,
.vad-toggle:focus-visible,
.vad-slider input[type='range']:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .vad-switch,
  .vad-switch span {
    transition: none;
  }
}

.microphone-device-select {
  width: 100%;
}

.microphone-device-select :deep(.el-select__wrapper) {
  min-height: 42px;
  padding: 0.45rem 0.7rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(23, 107, 77, 0.16);
  border-radius: 11px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.microphone-device-select :deep(.el-select__wrapper:hover) {
  border-color: rgba(15, 118, 110, 0.38);
}

.microphone-device-select :deep(.el-select__wrapper.is-focused) {
  background: #fff;
  border-color: rgba(45, 191, 193, 0.78);
  box-shadow: 0 0 0 3px rgba(45, 191, 193, 0.13);
}

.microphone-device-select :deep(.el-select__selected-item) {
  color: #172033;
  font-size: 0.84rem;
  font-weight: 720;
}

.microphone-device-select.is-disabled :deep(.el-select__wrapper) {
  cursor: wait;
  opacity: 0.58;
}


.status-dot {
  width: 6px;
  height: 6px;
  background: currentColor;
  border-radius: 50%;
}










@media (max-width: 768px) {
  .read-along-container {
    bottom: 78px;
    padding: 0 0.625rem;
  }

  .read-along-main,
  .read-along-main.read-mode-following {
    grid-template-columns: max-content minmax(0, 1fr) max-content;
    width: 100%;
    max-width: 100%;
    min-height: 48px;
    gap: 0.375rem;
    padding: 0.3125rem 0.375rem;
    border-radius: var(--radius-pill);
  }

  .mode-menu-trigger {
    height: 36px;
    padding: 0 var(--space-3);
  }

  .mode-menu-label {
    display: none;
  }

  .read-along-stage {
    min-width: 0;
    gap: 0.35rem;
    padding: 0;
  }

  .read-along-stage :deep(.sound-creature) {
    width: 46px;
    height: 46px;
    flex-basis: 46px;
  }

  .read-along-stage-copy span {
    display: none;
  }

  .read-along-stage-copy {
    min-width: 2.8rem;
  }


  .recording-library-summary {
    display: none;
  }

  .recording-tool-button {
    flex: 0 0 34px;
    width: 34px;
    min-width: 34px;
    height: 34px;
    min-height: 34px;
  }

  .attempt-actions {
    gap: var(--space-1);
  }

  .attempt-button {
    height: 34px;
    min-height: 34px;
    padding: 0 var(--space-3);
  }






}

</style>
